import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Banknote, Sparkles, RefreshCw, Link2, ShieldCheck, TestTube2, Radar } from "lucide-react";
import { toast } from "sonner";

function formatUsd(n) {
    return `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCrypto(n) {
    return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

const TX_EXPLORERS = {
    USDT_TRC20: (tx) => `https://tronscan.org/#/transaction/${tx}`,
    USDT_ERC20: (tx) => `https://etherscan.io/tx/${tx}`,
    USDC_ERC20: (tx) => `https://etherscan.io/tx/${tx}`,
};

function ManualTopupDialog({ users, onDone }) {
    const [form, setForm] = useState({ user_id: "", amount: "", currency: "USDT", tx_hash: "", note: "" });
    const [open, setOpen] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/transactions/manual-topup", {
                ...form,
                amount: parseFloat(form.amount),
            });
            toast.success("Bakiye eklendi");
            setOpen(false);
            setForm({ user_id: "", amount: "", currency: "USDT", tx_hash: "", note: "" });
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="manual-topup-btn" className="bg-[#00b67a] hover:bg-[#00b67a]/90 text-white">
                    <Banknote className="w-4 h-4 mr-2" /> Manuel bakiye ekle
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle>Kullanıcı cüzdanına bakiye ekle</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="manual-topup-form">
                    <div className="space-y-1.5">
                        <Label>Kullanıcı</Label>
                        <select
                            value={form.user_id}
                            onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                            data-testid="topup-user-select"
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="">— Kullanıcı seçin —</option>
                            {users.filter((u) => u.role === "user").map((u) => (
                                <option key={u.id} value={u.id}>{u.full_name} · {u.email}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Tutar (USD)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={form.amount}
                                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                                data-testid="topup-amount"
                                className="bg-background border-white/10"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Para birimi</Label>
                            <select
                                value={form.currency}
                                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                                data-testid="topup-currency-select"
                                className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                            >
                                <option>USDT_TRC20</option><option>USDT_ERC20</option><option>USDC_ERC20</option><option>USD</option>
                            </select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>TX hash (opsiyonel)</Label>
                        <Input
                            value={form.tx_hash}
                            onChange={(e) => setForm((f) => ({ ...f, tx_hash: e.target.value }))}
                            data-testid="topup-tx-hash"
                            className="bg-background border-white/10"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Not</Label>
                        <Textarea
                            rows={2}
                            value={form.note}
                            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                            data-testid="topup-note"
                            className="bg-background border-white/10"
                        />
                    </div>
                    <Button type="submit" data-testid="topup-submit" className="w-full bg-primary text-primary-foreground">
                        Cüzdana ekle
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CashbackDialog({ users, onDone }) {
    const [form, setForm] = useState({ user_id: "", amount: "", note: "" });
    const [open, setOpen] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/transactions/cashback-payout", {
                ...form,
                amount: parseFloat(form.amount),
            });
            toast.success("Cashback ödendi");
            setOpen(false);
            setForm({ user_id: "", amount: "", note: "" });
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="cashback-btn" className="border-[#2FA57C]/40 text-[#2FA57C] hover:bg-[#2FA57C]/10">
                    <Sparkles className="w-4 h-4 mr-2" /> Cashback öde
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle>Whale cashback ödemesi</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="cashback-form">
                    <div className="space-y-1.5">
                        <Label>Kullanıcı</Label>
                        <select
                            value={form.user_id}
                            onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                            data-testid="cashback-user-select"
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="">— Kullanıcı seçin —</option>
                            {users.filter((u) => u.role === "user").map((u) => (
                                <option key={u.id} value={u.id}>{u.full_name} · {u.email}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Tutar (USDT)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                            data-testid="cashback-amount"
                            className="bg-background border-white/10"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Not</Label>
                        <Textarea
                            rows={2}
                            value={form.note}
                            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                            data-testid="cashback-note"
                            className="bg-background border-white/10"
                        />
                    </div>
                    <Button type="submit" data-testid="cashback-submit" className="w-full bg-primary text-primary-foreground">
                        Cashback ödemesini gönder
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CreateTestOrderDialog({ users, onDone }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        user_id: "",
        currency: "USDT_TRC20",
        amount_usd: "1",
    });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/admin/orders/create-test", {
                user_id: form.user_id,
                currency: form.currency,
                amount_usd: Number(form.amount_usd || 1),
            });
            toast.success("$1 test siparişi oluşturuldu");
            setOpen(false);
            setForm({ user_id: "", currency: "USDT_TRC20", amount_usd: "1" });
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    data-testid="create-test-order-btn"
                    variant="outline"
                    className="border-primary/40 text-primary hover:bg-primary/10"
                >
                    <TestTube2 className="w-4 h-4 mr-2" /> $1 test ödeme
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle>$1 gerçek zincir test siparişi</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="create-test-order-form">
                    <div className="space-y-1.5">
                        <Label>Kullanıcı</Label>
                        <select
                            value={form.user_id}
                            onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                            data-testid="test-order-user-select"
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                            required
                        >
                            <option value="">— Kullanıcı seçin —</option>
                            {users.filter((u) => u.role === "user").map((u) => (
                                <option key={u.id} value={u.id}>{u.full_name} · {u.email}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Para birimi</Label>
                            <select
                                value={form.currency}
                                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                                data-testid="test-order-currency-select"
                                className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm"
                            >
                                <option>USDT_TRC20</option>
                                <option>USDT_ERC20</option>
                                <option>USDC_ERC20</option>
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Tutar (USD)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="1"
                                max="20"
                                value={form.amount_usd}
                                onChange={(e) => setForm((f) => ({ ...f, amount_usd: e.target.value }))}
                                data-testid="test-order-amount"
                                className="bg-background border-white/10"
                                required
                            />
                        </div>
                    </div>
                    <Button
                        type="submit"
                        data-testid="test-order-submit"
                        disabled={loading}
                        className="w-full bg-primary text-primary-foreground"
                    >
                        {loading ? "Oluşturuluyor..." : "Test siparişi oluştur"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ForceCreditDialog({ order, onDone, onClose }) {
    const [txHash, setTxHash] = useState(order?.tx_hash || "");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setTxHash(order?.tx_hash || "");
    }, [order?.id, order?.tx_hash]);

    const submit = async (e) => {
        e.preventDefault();
        if (!order?.id) return;
        setLoading(true);
        try {
            await api.post("/admin/orders/force-credit", {
                order_id: order.id,
                tx_hash: txHash,
            });
            toast.success("Sipariş başarıyla onaylandı ve kredilendi");
            onDone();
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-card border-white/10 max-w-lg">
                <DialogHeader>
                    <DialogTitle>Force Credit by TxID</DialogTitle>
                    <DialogDescription data-testid="force-credit-description">
                        TxID yalnızca bu siparişin adresinde ve doğru tutarda doğrulanırsa kredi verilir.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="force-credit-form">
                    <div className="rounded-xl border border-white/10 bg-background/50 p-3 space-y-2 text-xs">
                        <p data-testid="force-credit-order-id"><strong>Sipariş:</strong> {order.id}</p>
                        <p data-testid="force-credit-order-user"><strong>Kullanıcı:</strong> {order.user_email || order.user_id}</p>
                        <p data-testid="force-credit-order-amount"><strong>Beklenen:</strong> {formatCrypto(order.amount_crypto)} {order.currency}</p>
                        <p data-testid="force-credit-order-address" className="break-all"><strong>Adres:</strong> {order.address}</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label>TxID / Tx Hash</Label>
                        <Input
                            value={txHash}
                            onChange={(e) => setTxHash(e.target.value)}
                            data-testid="force-credit-tx-hash"
                            className="bg-background border-white/10 font-mono"
                            placeholder="0x... / <SECRET>..."
                            required
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="force-credit-submit"
                        className="w-full bg-primary text-primary-foreground"
                    >
                        {loading ? "Doğrulanıyor..." : "Ödemeyi doğrula ve kredile"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminTransactions() {
    const [txns, setTxns] = useState([]);
    const [users, setUsers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [traceTxHash, setTraceTxHash] = useState("");
    const [traceCurrency, setTraceCurrency] = useState("");
    const [traceLoading, setTraceLoading] = useState(false);
    const [traceResult, setTraceResult] = useState(null);
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [recoveryResult, setRecoveryResult] = useState(null);
    const [mnemonicStatus, setMnemonicStatus] = useState(null);
    const [reconcileLoading, setReconcileLoading] = useState(false);
    const [reconcileJob, setReconcileJob] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [txRes, userRes, orderRes] = await Promise.all([
                api.get("/admin/transactions"),
                api.get("/admin/users"),
                api.get("/admin/orders"),
            ]);
            setTxns(txRes.data || []);
            setUsers(userRes.data || []);
            setOrders(orderRes.data || []);

            try {
                const { data } = await api.get("/admin/wallet-ops/mnemonic-status");
                setMnemonicStatus(data);
            } catch {
                setMnemonicStatus(null);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Veriler alınamadı");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openForceCredit = (order) => {
        setSelectedOrder(order);
    };

    const closeForceCredit = () => {
        setSelectedOrder(null);
    };

    const runTrace = async (e) => {
        e.preventDefault();
        if (!traceTxHash.trim()) {
            toast.error("TxID girin");
            return;
        }
        setTraceLoading(true);
        setTraceResult(null);
        try {
            const { data } = await api.post("/admin/orders/trace-tx", {
                tx_hash: traceTxHash.trim(),
                currency: traceCurrency || undefined,
            });
            setTraceResult(data);
            toast.success("TxID taraması tamamlandı");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "TxID taraması başarısız");
        } finally {
            setTraceLoading(false);
        }
    };

    const runRecoveryScan = async () => {
        setRecoveryLoading(true);
        try {
            const { data } = await api.post("/admin/recovery/scan", {
                window_days: 90,
                include_zero_balances: true,
            });
            setRecoveryResult(data);
            toast.success("90 günlük cüzdan taraması tamamlandı");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Tarama başarısız");
        } finally {
            setRecoveryLoading(false);
        }
    };

    const runDepositReconcileNow = async () => {
        setReconcileLoading(true);
        try {
            const { data } = await api.post("/admin/deposits/reconcile-now");
            const jobId = data?.job_id;
            if (!jobId) {
                toast.error("Job başlatılamadı");
                return;
            }
            setReconcileJob({ id: jobId, status: "queued", users_total: data?.users_queued || 0, users_done: 0, new_topup_transactions: 0 });
            toast.success(`Tarama kuyruğa alındı (${data?.users_queued || 0} kullanıcı)`);

            for (let i = 0; i < 60; i += 1) {
                // 60 * 1.5s ~= 90s
                await new Promise((r) => setTimeout(r, 1500));
                const { data: st } = await api.get(`/admin/deposits/reconcile-status/${jobId}`);
                setReconcileJob(st);
                if (st?.status === "completed") {
                    toast.success(`Tarama bitti · yeni kredi: ${st?.new_topup_transactions ?? 0}`);
                    await load();
                    return;
                }
                if (st?.status === "failed") {
                    toast.error(st?.error || "Tarama başarısız");
                    return;
                }
            }
            toast.message("Tarama devam ediyor. Birazdan tekrar kontrol edin.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reconcile başarısız");
        } finally {
            setReconcileLoading(false);
        }
    };

    return (
        <div data-testid="admin-transactions" className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">İşlemler</h1>
                    <p className="text-muted-foreground mt-1">
                        Ödeme mutabakatı, force-credit kurtarma ve cüzdan hareketleri.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        data-testid="admin-transactions-refresh"
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        className="border-white/10"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Yenile
                    </Button>
                    <CreateTestOrderDialog users={users} onDone={load} />
                    <Button
                        data-testid="admin-reconcile-now-btn"
                        variant="outline"
                        onClick={runDepositReconcileNow}
                        disabled={reconcileLoading}
                        className="border-emerald-400/30 text-emerald-400 hover:bg-emerald-500/10"
                    >
                        <Radar className={`w-4 h-4 mr-2 ${reconcileLoading ? "animate-spin" : ""}`} />
                        {reconcileLoading ? "Taranıyor..." : "Ödemeleri şimdi tara"}
                    </Button>
                    <ManualTopupDialog users={users} onDone={load} />
                    <CashbackDialog users={users} onDone={load} />
                </div>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4" data-testid="payment-guard-rail-note">
                <p className="text-sm font-medium">Para kaybını önleme paneli</p>
                <p className="text-xs text-muted-foreground mt-1">
                    Tüm pending siparişler aşağıda görünür. Önce TxID ile eşleşmeyi kontrol edin,
                    gerekirse sadece ilgili siparişte <strong>Force Credit</strong> kullanın.
                </p>
                <div className="mt-2 text-xs text-muted-foreground" data-testid="mnemonic-security-note">
                    <strong>Mnemonic güvenliği:</strong> Plaintext kurtarma tümceciği panelde gösterilmez.
                    Bu veri ele geçirilirse tüm cüzdanlar boşaltılabilir. Bu yüzden yalnızca fingerprint/status gösteriyoruz.
                    {mnemonicStatus?.configured && (
                        <span data-testid="mnemonic-fingerprint" className="ml-1">
                            (configured · fp: {mnemonicStatus.fingerprint})
                        </span>
                    )}
                </div>
                {reconcileJob && (
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="reconcile-job-status">
                        Reconcile Job: {reconcileJob.id?.slice(0, 8)}… · durum: {reconcileJob.status} ·
                        {" "}ilerleme: {reconcileJob.users_done || 0}/{reconcileJob.users_total || 0} ·
                        {" "}yeni kredi: {reconcileJob.new_topup_transactions || 0}
                    </div>
                )}
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl p-4" data-testid="tx-trace-panel">
                <h2 className="font-semibold mb-2">TxID İz Sürme (Para nereye gitti?)</h2>
                <form onSubmit={runTrace} className="grid md:grid-cols-[1fr_190px_170px] gap-2 items-end" data-testid="trace-tx-form">
                    <div className="space-y-1">
                        <Label>TxID / Tx Hash</Label>
                        <Input
                            value={traceTxHash}
                            onChange={(e) => setTraceTxHash(e.target.value)}
                            data-testid="trace-tx-hash-input"
                            placeholder="0x... / <SECRET>..."
                            className="bg-background border-white/10 font-mono"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label>Ağ filtresi (opsiyonel)</Label>
                        <select
                            value={traceCurrency}
                            onChange={(e) => setTraceCurrency(e.target.value)}
                            data-testid="trace-currency-select"
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm h-10"
                        >
                            <option value="">Tümü</option>
                            <option value="USDT_TRC20">USDT_TRC20</option>
                            <option value="USDT_ERC20">USDT_ERC20</option>
                            <option value="USDC_ERC20">USDC_ERC20</option>
                        </select>
                    </div>
                    <Button
                        type="submit"
                        disabled={traceLoading}
                        data-testid="trace-tx-submit"
                        className="bg-primary text-primary-foreground"
                    >
                        {traceLoading ? "Taranıyor..." : "TX tara"}
                    </Button>
                </form>

                {traceResult && (
                    <div className="mt-3 rounded-xl border border-white/10 bg-background/40 p-3 text-xs space-y-2" data-testid="trace-result-panel">
                        <p data-testid="trace-recommendation"><strong>Öneri:</strong> {traceResult.recommendation}</p>
                        {traceResult.matched_paid_order && (
                            <p data-testid="trace-paid-order">✅ Zaten işlenen sipariş: {traceResult.matched_paid_order.order_id} · {traceResult.matched_paid_order.user_email || traceResult.matched_paid_order.user_id}</p>
                        )}
                        {traceResult.matched_pending_order_on_chain && (
                            <p data-testid="trace-pending-order">🟡 Zincirde bulunan pending sipariş: {traceResult.matched_pending_order_on_chain.order_id} · {traceResult.matched_pending_order_on_chain.user_email || traceResult.matched_pending_order_on_chain.user_id}</p>
                        )}
                        {!traceResult.matched_paid_order && !traceResult.matched_pending_order_on_chain && (
                            <p data-testid="trace-not-found">❌ Bu TX sipariş adreslerinde bulunamadı.</p>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl p-4" data-testid="recovery-scan-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-semibold">Eski order cüzdan taraması (90 gün)</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            Tüm adresleri listeler (0 bakiyeler dahil). Bakiye olan adresleri öncelikli gösterir.
                        </p>
                    </div>
                    <Button
                        onClick={runRecoveryScan}
                        disabled={recoveryLoading}
                        data-testid="recovery-scan-run"
                        className="bg-primary text-primary-foreground"
                    >
                        {recoveryLoading ? "Taranıyor..." : "90 gün taramayı çalıştır"}
                    </Button>
                </div>

                {recoveryResult && (
                    <div className="mt-3 space-y-3" data-testid="recovery-scan-result">
                        <div className="text-xs text-muted-foreground" data-testid="recovery-summary">
                            Toplam adres: {recoveryResult.summary?.total_addresses || 0} ·
                            Bakiye&gt;0: {recoveryResult.summary?.non_zero_addresses || 0} ·
                            Toplam stablecoin: {recoveryResult.summary?.total_stablecoin_balance || 0}
                        </div>
                        <div className="text-xs" data-testid="gas-model-note">
                            <p>
                                <strong>Manuel model:</strong> Fee için ETH/TRX&apos;i ilgili <em>fee_send_to</em> adresine gönderin,
                                sonra Sweep kullanın.
                            </p>
                            <p>
                                <strong>Otomatik model:</strong> `COLD_WALLET_ETH_PK` / `COLD_WALLET_TRON_PK` tanımlıysa sistem fee advance yapar.
                            </p>
                            <p className="text-muted-foreground mt-1">
                                ETH auto: {recoveryResult.gas_automation?.eth_enabled ? "Açık" : "Kapalı"} ·
                                TRON auto: {recoveryResult.gas_automation?.tron_enabled ? "Açık" : "Kapalı"}
                            </p>
                        </div>

                        <div className="overflow-x-auto border border-white/10 rounded-xl" data-testid="recovery-table-wrap">
                            <table className="w-full text-xs" data-testid="recovery-table">
                                <thead className="bg-white/[0.03] uppercase text-[10px] text-muted-foreground">
                                    <tr>
                                        <th className="text-left p-2">Kullanıcı</th>
                                        <th className="text-left p-2">Para birimi</th>
                                        <th className="text-right p-2">Bakiye</th>
                                        <th className="text-left p-2">Adres</th>
                                        <th className="text-left p-2">Fee talimatı</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {(recoveryResult.rows || []).map((r, idx) => (
                                        <tr key={`${r.address}-${idx}`} data-testid={`recovery-row-${idx}`}>
                                            <td className="p-2 font-mono">{r.user_email || r.user_id}</td>
                                            <td className="p-2">{r.currency}</td>
                                            <td className="p-2 text-right font-semibold">{Number(r.balance || 0).toFixed(6)}</td>
                                            <td className="p-2 font-mono break-all">{r.address}</td>
                                            <td className="p-2">
                                                <p className="font-mono">{r.fee_amount_recommended} {r.fee_asset}</p>
                                                <p className="text-muted-foreground break-all">fee gönderim: {r.fee_send_to}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="admin-orders-table-wrap">
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-semibold">Siparişler (pending dahil)</h2>
                    <span className="text-xs text-muted-foreground" data-testid="orders-count-badge">
                        {orders.length} sipariş
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-orders-table">
                        <thead className="bg-white/[0.02] text-xs font-mono uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Durum</th>
                                <th className="text-left p-3">Kullanıcı</th>
                                <th className="text-left p-3">Sipariş</th>
                                <th className="text-left p-3 hidden lg:table-cell">Adres / Tx</th>
                                <th className="text-right p-3">Aksiyon</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {orders.map((o) => {
                                const canForce = ["pending", "expired", "failed"].includes(o.status);
                                const txLink = o.tx_hash ? TX_EXPLORERS[o.currency]?.(o.tx_hash) : null;
                                return (
                                    <tr key={o.id} data-testid={`order-row-${o.id}`}>
                                        <td className="p-3">
                                            <span
                                                data-testid={`order-status-${o.id}`}
                                                className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-mono uppercase ${
                                                    o.status === "paid"
                                                        ? "bg-emerald-500/15 text-emerald-400"
                                                        : o.status === "pending"
                                                        ? "bg-yellow-500/15 text-yellow-400"
                                                        : "bg-white/10 text-muted-foreground"
                                                }`}
                                            >
                                                {o.status}
                                            </span>
                                            {o.is_test_order && (
                                                <p className="text-[10px] text-primary mt-1" data-testid={`order-test-flag-${o.id}`}>TEST</p>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs font-mono" data-testid={`order-user-${o.id}`}>
                                            {o.user_email || o.user_id}
                                        </td>
                                        <td className="p-3">
                                            <p className="font-medium capitalize" data-testid={`order-plan-${o.id}`}>
                                                {(o.plan || "-").replace("-", " ")}{o.platform ? ` · ${o.platform}` : ""}
                                            </p>
                                            <p className="text-[11px] font-mono text-muted-foreground" data-testid={`order-amount-${o.id}`}>
                                                {formatCrypto(o.amount_crypto)} {o.currency} · {formatUsd(o.amount_usd)}
                                            </p>
                                        </td>
                                        <td className="p-3 hidden lg:table-cell">
                                            <p className="text-[11px] font-mono text-muted-foreground break-all" data-testid={`order-address-${o.id}`}>
                                                {o.address}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <p className="text-[11px] font-mono" data-testid={`order-tx-${o.id}`}>{o.tx_hash || "—"}</p>
                                                {txLink && (
                                                    <a
                                                        href={txLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        data-testid={`order-tx-link-${o.id}`}
                                                        className="text-primary inline-flex items-center gap-1 text-[11px]"
                                                    >
                                                        <Link2 className="w-3 h-3" /> aç
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3 text-right">
                                            {canForce ? (
                                                <Button
                                                    size="sm"
                                                    onClick={() => openForceCredit(o)}
                                                    data-testid={`force-credit-open-${o.id}`}
                                                    className="bg-primary text-primary-foreground"
                                                >
                                                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Force Credit
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-muted-foreground" data-testid={`order-action-none-${o.id}`}>—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {orders.length === 0 && (
                        <p className="p-10 text-center text-sm text-muted-foreground" data-testid="orders-empty">Henüz sipariş yok.</p>
                    )}
                </div>
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-semibold">Kredilenen işlemler</h2>
                    <span className="text-xs text-muted-foreground" data-testid="txns-count-badge">{txns.length} işlem</span>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">Tip</th>
                            <th className="text-left p-4 hidden md:table-cell">Kullanıcı</th>
                            <th className="text-right p-4">Tutar</th>
                            <th className="text-left p-4 hidden lg:table-cell">TX hash / Not</th>
                            <th className="text-right p-4">Tarih</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {txns.map((t) => {
                            const u = users.find((x) => x.id === t.user_id);
                            return (
                                <tr key={t.id} data-testid={`txn-row-${t.id}`}>
                                    <td className="p-4">
                                        <p className="font-medium capitalize">{t.type.replace("_", " ")}</p>
                                        <p className="text-[10px] font-mono text-muted-foreground">
                                            {t.currency} · {t.status}
                                        </p>
                                    </td>
                                    <td className="p-4 hidden md:table-cell text-xs font-mono">
                                        {u?.email || t.user_id.slice(0, 8)}
                                    </td>
                                    <td className={`p-4 text-right font-display font-semibold ${t.type === "spend" ? "" : "text-[#00b67a]"}`}>
                                        {t.type === "spend" ? "-" : "+"}{formatUsd(t.amount)}
                                    </td>
                                    <td className="p-4 hidden lg:table-cell text-xs font-mono text-muted-foreground truncate max-w-xs">
                                        {t.tx_hash || t.note || "—"}
                                    </td>
                                    <td className="p-4 text-right text-xs font-mono text-muted-foreground">
                                        {new Date(t.created_at).toLocaleDateString()}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {txns.length === 0 && (
                    <p className="p-12 text-center text-sm text-muted-foreground">Henüz işlem yok.</p>
                )}
            </div>

            <ForceCreditDialog order={selectedOrder} onDone={load} onClose={closeForceCredit} />
        </div>
    );
}
